import React, { FunctionComponent } from 'react'

import { isDate } from 'date-fns'
import { useField } from 'formik'
import { SpaceProps, WidthProps } from 'styled-system'

import { DatePicker } from '@/atoms/input'
import { convertDateToString } from '@/helpers/date'

import FieldWrapper from './field-wrapper'

export interface DatePickerProps {
  label?: string
  fieldProps?: SpaceProps & WidthProps
  name?: string
  onChange?: (date: Date | { from: Date; to: Date }) => void
  maxDate?: Date | string
  minDate?: Date | string
  selectsRange?: boolean
  disabled?: boolean
  tooltip?: string
  hideErrorMessage?: boolean
}

const DatePickerField: FunctionComponent<DatePickerProps> = ({
  label,
  fieldProps,
  name,
  onChange,
  minDate,
  maxDate,
  selectsRange,
  disabled,
  hideErrorMessage,
  tooltip,
}: DatePickerProps) => {
  const [field, meta, helpers] = useField(name)
  const hasError: boolean = meta.touched && !!meta.error
  const startDate =
    typeof field.value === 'object' && field.value !== null && !isDate(field.value) ? field?.value.from : field.value || null
  const endDate = typeof field.value === 'object' && field.value !== null && !isDate(field.value) ? field?.value.to : null

  const handleChange = (value: Date | { from: Date; to: Date }): void => {
    let formattedValue: string | { from: string; to: string }
    if (typeof value === 'object' && value !== null && 'from' in value && 'to' in value) {
      formattedValue = {
        from: convertDateToString(value.from),
        to: convertDateToString(value.to),
      }
    } else {
      formattedValue = convertDateToString(value as Date)
    }
    onChange && onChange(value)
    helpers?.setValue(formattedValue)
  }

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
      <DatePicker
        id={field.name}
        hasError={hasError}
        startDate={startDate}
        endDate={endDate}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        selectsRange={selectsRange}
        disabled={disabled}
      />
    </FieldWrapper>
  )
}

DatePickerField.displayName = 'DatePickerField'

export default DatePickerField
